/**
 * Period Timezone Utilities
 * Handles timezone conversion for peri    // Create date in user's timezone
    const userTime = new Date();
    userTime.setHours(hours, minutes, 0, 0);s based on school timezone context
 */

/**
 * Convert period time from school timezone to user's display timezone
 * @param {string} timeString - Time in HH:MM:SS or HH:MM format  
 * @param {string} schoolTimezone - School's timezone (e.g., 'America/New_York')
 * @param {string} userTimezone - User's display timezone (optional, uses Account Settings if not provided)
 * @returns {string} - Converted time in HH:MM format
 */
export const convertPeriodTimeToUserTimezone = (timeString, schoolTimezone, userTimezone = null) => {
  if (!timeString || !schoolTimezone) {
    console.log('❌ Missing parameters for conversion:', { timeString, schoolTimezone });
    return timeString;
  }
  
  try {
    // Import timezone utilities - use import instead of require
    import('./timezoneUtils').then(module => {
      const { getCurrentTimezoneConfig, getConfiguredTimezone } = module;
      return { getCurrentTimezoneConfig, getConfiguredTimezone };
    });
    
    // For now, use direct approach - this needs to be synchronous
    const storedTimezone = localStorage.getItem('arcattend_timezone_config');
    const currentConfig = storedTimezone || 'LOCAL';
    
    // Simple timezone mapping for common cases
    const timezoneMap = {
      'LOCAL': undefined,
      'UTC': 'UTC',
      'EST': 'America/New_York',
      'PST': 'America/Los_Angeles',
      'CST': 'America/Chicago',
      'MST': 'America/Denver'
    };
    
    let displayTimezone = userTimezone;
    if (!displayTimezone) {
      if (timezoneMap[currentConfig]) {
        displayTimezone = timezoneMap[currentConfig];
      } else {
        displayTimezone = currentConfig; // Assume it's already a valid timezone identifier
      }
    }
    
    // Normalize time format - handle "HH:MM:SS", "HH:MM", or "H:MMAM/PM" formats
    let normalizedTime = timeString;
    
    // Check if it's already in 12-hour format (ends with AM/PM)
    if (timeString.match(/\d+:\d+[AP]M$/i)) {
      // Convert 12-hour format to 24-hour format first
      const match = timeString.match(/(\d+):(\d+)([AP]M)$/i);
      if (match) {
        let hours = parseInt(match[1]);
        const minutes = match[2];
        const period = match[3].toUpperCase();
        
        if (period === 'PM' && hours !== 12) {
          hours += 12;
        } else if (period === 'AM' && hours === 12) {
          hours = 0;
        }
        
        normalizedTime = `${hours.toString().padStart(2, '0')}:${minutes}`;
      }
    } else {
      // Handle "HH:MM:SS" or "HH:MM" format
      normalizedTime = timeString.split(':').slice(0, 2).join(':');
    }
    
    console.log('🔍 Timezone conversion debug:', { 
      originalTime: timeString,
      normalizedTime,
      schoolTimezone,
      displayTimezone,
      currentConfig,
      userTimezone
    });
    
    // If user is using LOCAL timezone, no conversion needed
    if (currentConfig === 'LOCAL') {
      console.log('📍 Using LOCAL timezone, no conversion needed');
      return normalizedTime;
    }
    
    // If same timezone or no display timezone, no conversion needed
    if (!displayTimezone || displayTimezone === schoolTimezone) {
      console.log('📍 Same timezone or no display timezone, no conversion needed');
      return normalizedTime;
    }
    
    console.log('🔄 Converting period time:', { normalizedTime, schoolTimezone, displayTimezone });
    
    // Parse the time string (format: "HH:MM")
    const [hours, minutes] = normalizedTime.split(':').map(Number);
    
    if (isNaN(hours) || isNaN(minutes)) {
      console.warn('❌ Invalid time format:', timeString);
      return normalizedTime;
    }
    
    // Use today's date to ensure we handle DST correctly
    const today = new Date();
    
    // Create a base date with the period time
    const baseTime = new Date(`2023-06-15T${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00`);
    
    // Get the offset difference between the two timezones for today
    const schoolOffset = getTimezoneOffsetMinutes(today, schoolTimezone);
    const userOffset = getTimezoneOffsetMinutes(today, displayTimezone);
    const offsetDifference = userOffset - schoolOffset;
    
    // Apply the offset difference to convert from school timezone to user timezone
    const adjustedTime = new Date(baseTime.getTime() + (offsetDifference * 60000));
    
    // Format as HH:MM
    const userHours = adjustedTime.getHours();
    const userMinutes = adjustedTime.getMinutes();
    const result = `${userHours.toString().padStart(2, '0')}:${userMinutes.toString().padStart(2, '0')}`;
    
    console.log('🔧 Time conversion result:', { 
      original: timeString,
      normalized: normalizedTime, 
      schoolOffset,
      userOffset,
      offsetDifference,
      converted: result 
    });
    
    return result;
    
  } catch (error) {
    console.warn('❌ Error converting period time:', error);
    return timeString; // Fallback to original time
  }
};

// Helper function to get timezone offset in minutes
const getTimezoneOffsetMinutes = (date, timezone) => {
  const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
  const tzDate = new Date(date.toLocaleString('en-US', { timeZone: timezone }));
  return (tzDate.getTime() - utcDate.getTime()) / (1000 * 60);
};

/**
 * Convert period time from user's input timezone to school timezone for storage
 * @param {string} timeString - Time in HH:MM:SS or HH:MM format from user input
 * @param {string} schoolTimezone - School's timezone (e.g., 'America/New_York')  
 * @param {string} userTimezone - User's input timezone (optional, uses Account Settings if not provided)
 * @returns {string} - Converted time in HH:MM:SS format for database storage
 */
export const convertPeriodTimeToSchoolTimezone = (timeString, schoolTimezone, userTimezone = null) => {
  if (!timeString || !schoolTimezone) return timeString;
  
  try {
    // Import timezone utilities
    const { getCurrentTimezoneConfig, getConfiguredTimezone } = require('./timezoneUtils');
    
    // Get user's input timezone (from Account Settings)
    const inputTimezone = userTimezone || getConfiguredTimezone();
    
    // If user is using LOCAL timezone or same as school, no conversion needed
    if (!inputTimezone || inputTimezone === schoolTimezone || getCurrentTimezoneConfig() === 'LOCAL') {
      // Ensure proper format for database (HH:MM:SS)
      return timeString.includes(':') && timeString.split(':').length === 2 
        ? `${timeString}:00` 
        : timeString;
    }
    
    // Create a date object in user's timezone
    const [hours, minutes] = timeString.split(':').map(Number);
    
    // Create date in user's timezone
    const userTime = new Date();
    userTime.setHours(hours, minutes, 0, 0);
    
    // Convert to school's timezone
    const schoolTime = new Date(userTime.toLocaleString('en-US', { timeZone: schoolTimezone }));
    
    // Format back to HH:MM:SS
    const schoolHours = schoolTime.getHours();
    const schoolMinutes = schoolTime.getMinutes();
    
    return `${schoolHours.toString().padStart(2, '0')}:${schoolMinutes.toString().padStart(2, '0')}:00`;
    
  } catch (error) {
    console.warn('Error converting period time to school timezone:', error);
    // Fallback: ensure proper format
    return timeString.includes(':') && timeString.split(':').length === 2 
      ? `${timeString}:00` 
      : timeString;
  }
};

/**
 * Format period time for display with timezone context
 * Shows both school time and user time if different
 * @param {string} timeString - Time in HH:MM:SS, HH:MM format, or "HH:MM - HH:MM" range format
 * @param {string} schoolTimezone - School's timezone  
 * @param {object} options - Display options
 * @returns {string} - Formatted time string
 */
export const formatPeriodTimeWithTimezone = (timeString, schoolTimezone, options = {}) => {
  if (!timeString) return '';
  
  const {
    showBothTimezones = false,
    format12Hour = true,
    showTimezoneLabel = false
  } = options;
  
  try {
    // Handle time range format "HH:MM - HH:MM" or "HH:MMAM - HH:MMPM"
    if (timeString.includes(' - ')) {
      const [startTime, endTime] = timeString.split(' - ');
      const formattedStart = formatSingleTime(startTime.trim(), schoolTimezone, { format12Hour, showTimezoneLabel });
      const formattedEnd = formatSingleTime(endTime.trim(), schoolTimezone, { format12Hour, showTimezoneLabel });
      return `${formattedStart} - ${formattedEnd}`;
    }
    
    // Handle single time
    return formatSingleTime(timeString, schoolTimezone, { format12Hour, showTimezoneLabel, showBothTimezones });
    
  } catch (error) {
    console.warn('Error formatting period time with timezone:', error);
    return timeString;
  }
};

/**
 * Helper function to format a single time with timezone context
 */
const formatSingleTime = (timeString, schoolTimezone, options = {}) => {
  const { format12Hour = true, showTimezoneLabel = false, showBothTimezones = false } = options;
  
  // Import timezone utilities
  const { getCurrentTimezoneConfig, getConfiguredTimezone } = require('./timezoneUtils');
  
  const userTimezone = getConfiguredTimezone();
  const isLocalUser = getCurrentTimezoneConfig() === 'LOCAL';
  
  // Extract time components - handle both "HH:MM" and "HH:MMAM/PM" formats
  let hours, minutes;
  if (timeString.includes('AM') || timeString.includes('PM')) {
    // Handle 12-hour format input like "10:00AM"
    const isPM = timeString.toUpperCase().includes('PM');
    const timeOnly = timeString.replace(/AM|PM/i, '').trim();
    const [h, m] = timeOnly.split(':').map(Number);
    hours = isPM && h !== 12 ? h + 12 : (!isPM && h === 12 ? 0 : h);
    minutes = m;
  } else {
    // Handle 24-hour format input like "10:00"
    [hours, minutes] = timeString.split(':').map(Number);
  }
  
  // Format the display time
  let displayTime;
  if (format12Hour) {
    const isPM = hours >= 12;
    const hour12 = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
    displayTime = `${hour12}:${minutes.toString().padStart(2, '0')}${isPM ? 'PM' : 'AM'}`;
  } else {
    displayTime = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  }
  
  // If user is local or same timezone, just show the time
  if (isLocalUser || !userTimezone || userTimezone === schoolTimezone) {
    return showTimezoneLabel ? `${displayTime} (School Time)` : displayTime;
  }
  
  // Show both timezones if requested and they're different
  if (showBothTimezones) {
    const convertedTime = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    const userTime = convertPeriodTimeToUserTimezone(convertedTime, schoolTimezone, userTimezone);
    const [userHours, userMinutes] = userTime.split(':').map(Number);
    
    let userDisplayTime;
    if (format12Hour) {
      const isPM = userHours >= 12;
      const hour12 = userHours > 12 ? userHours - 12 : userHours === 0 ? 12 : userHours;
      userDisplayTime = `${hour12}:${userMinutes.toString().padStart(2, '0')}${isPM ? 'PM' : 'AM'}`;
    } else {
      userDisplayTime = `${userHours.toString().padStart(2, '0')}:${userMinutes.toString().padStart(2, '0')}`;
    }
    
    return `${displayTime} (School) / ${userDisplayTime} (Your Time)`;
  }
  
  return displayTime;
};

/**
 * Get current period based on school's timezone
 * @param {Array} periods - Array of period objects with start_time and end_time
 * @param {string} schoolTimezone - School's timezone
 * @returns {object|null} - Current period or null if none active
 */
export const getCurrentPeriodInSchoolTimezone = (periods, schoolTimezone) => {
  if (!periods || !Array.isArray(periods) || !schoolTimezone) return null;
  
  try {
    // Get current time in school's timezone
    const now = new Date();
    const schoolTime = new Date(now.toLocaleString('en-US', { timeZone: schoolTimezone }));
    const currentMinutes = schoolTime.getHours() * 60 + schoolTime.getMinutes();
    
    // Find current period
    return periods.find(period => {
      const [startHours, startMinutes] = period.start_time.split(':').map(Number);
      const [endHours, endMinutes] = period.end_time.split(':').map(Number);
      
      const startTotalMinutes = startHours * 60 + startMinutes;
      const endTotalMinutes = endHours * 60 + endMinutes;
      
      return currentMinutes >= startTotalMinutes && currentMinutes < endTotalMinutes;
    });
    
  } catch (error) {
    console.warn('Error getting current period in school timezone:', error);
    return null;
  }
};

/**
 * Get next period based on school's timezone
 * @param {Array} periods - Array of period objects with start_time and end_time
 * @param {string} schoolTimezone - School's timezone
 * @returns {object|null} - Next period or null if none upcoming
 */
export const getNextPeriodInSchoolTimezone = (periods, schoolTimezone) => {
  if (!periods || !Array.isArray(periods) || !schoolTimezone) return null;
  
  try {
    // Get current time in school's timezone
    const now = new Date();
    const schoolTime = new Date(now.toLocaleString('en-US', { timeZone: schoolTimezone }));
    const currentMinutes = schoolTime.getHours() * 60 + schoolTime.getMinutes();
    
    // Find next period (earliest start time after current time)
    const upcomingPeriods = periods.filter(period => {
      const [startHours, startMinutes] = period.start_time.split(':').map(Number);
      const startTotalMinutes = startHours * 60 + startMinutes;
      return startTotalMinutes > currentMinutes;
    });
    
    if (upcomingPeriods.length === 0) return null;
    
    // Sort by start time and return the earliest
    return upcomingPeriods.sort((a, b) => {
      const [aHours, aMinutes] = a.start_time.split(':').map(Number);
      const [bHours, bMinutes] = b.start_time.split(':').map(Number);
      const aTotalMinutes = aHours * 60 + aMinutes;
      const bTotalMinutes = bHours * 60 + bMinutes;
      return aTotalMinutes - bTotalMinutes;
    })[0];
    
  } catch (error) {
    console.warn('Error getting next period in school timezone:', error);
    return null;
  }
};
